import React, { Component } from "react"
import PropTypes from "prop-types"
import isEqual from "lodash/isEqual"
import Highcharts from "highcharts/highstock"
import Exporting from "highcharts/modules/exporting"
import drilldown from "highcharts/modules/drilldown"
import { translate } from "react-i18next"
import { CHARTS } from "../../../constants"
import { getDrilldownSeries, getDrillDownRootSeries } from "../../../utils/drilldown-utils"
import { getOrdinalSuffix, formatDecimalForDisplay } from "../../../utils/formatting-utils"
import DrilldownShape from "../../../state/shapes/DrilldownShapes/DrilldownShape"

class DrilldownBarChart extends Component {
  componentDidMount = () => {
    this.createChart()
  }

  shouldComponentUpdate = (nextProps) => {
    return (
      this.props.scrollbarEnabled !== nextProps.scrollbarEnabled ||
      !isEqual(this.props.data, nextProps.data)
    )
  }

  componentDidUpdate = () => {
    this.createChart()
  }

  componentWillUnmount = () => {
    this.chart.destroy()
  }

  createChart = () => {
    drilldown(Highcharts)

    // Get the element into which to insert the chart
    const chartContainer = this.chartContainerRef

    const {
      t, xAxisTitle, yAxisTitle, scrollbarEnabled, data, tooltipFormatter, titleKey, subtitleKey
    } = this.props

    const fontSize = 11

    const seriesData = getDrillDownRootSeries(data, t)
    const drilldownData = getDrilldownSeries(data, t)

    const xAxisMaxSize = 4

    Exporting(Highcharts)

    /** Sets specific image export settings so that
     * the export doesn't contain scrollbars (i.e. it
     * shows everything in a tall image)
     */
    const updateExportSettings = (chart, barCount) => {
      const exportImageHeight = 40 * barCount

      chart.exporting.update({
        sourceHeight: exportImageHeight,
        chartOptions: {
          xAxis: {
            max: barCount - 1,
          },
        },
      })
    }

    // Create the chart
    this.chart = new Highcharts.Chart({
      chart: {
        renderTo: chartContainer,
        height: CHARTS.standardHeight - 28,
        type: "bar",
        animation: false,
        events: {
          drillup(e) {
            updateExportSettings(this, e.seriesOptions.data.length)
          },
          drilldown(e) {
            updateExportSettings(this, e.seriesOptions.data.length)
          },
        },
      },
      lang: {
        drillUpText: t("Back"),
        contextButtonTitle: t("Common.ChartDownloadOptions"),
      },
      title: {
        text: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
        y: -30
      },
      subtitle: {
        text: subtitleKey != null && subtitleKey.length > 0 ? t(subtitleKey) : null,
        y: -15
      },
      colors: CHARTS.colours,
      xAxis: {
        allowDecimals: false,
        type: "category",
        title: {
          text: xAxisTitle,
        },
        scrollbar: {
          enabled: scrollbarEnabled,
          liveRedraw: false,
        },
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
        tickLength: 0,
        min: 0,
        max: xAxisMaxSize,
      },
      yAxis: {
        title: {
          text: yAxisTitle ? t(yAxisTitle) : null,
        },
        gridLineDashStyle: "ShortDot",
      },
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
          },
        },
        series: {
          dataLabels: {
            enabled: true,
            formatter() {
              if (this.point) {
                const translatedKey = this.point.name || t(this.point.key)
                const formattedValue = formatDecimalForDisplay(this.y)
                const text = `${translatedKey}... ${formattedValue}%`
                // font size is in pixels, so if we divide the height of the bar by it
                // We should get amount of characters that can fit in the space
                const labelSpaceLength = this.point.shapeArgs.height / fontSize
                if (labelSpaceLength < text.length) {
                  return `${translatedKey.substring(0, labelSpaceLength)}... ${formattedValue}%`
                }
                return `${translatedKey} ${formattedValue}%`
              }
              return undefined
            },
            inside: true,
            minPointLength: 2,
            style: {
              color: "#333",
              textOutline: 0,
              fontWeight: "normal",
              fontSize,
            },
            align: "left",
            crop: true,
            allowOverlap: false,
          },
          colorByPoint: true,
          groupPadding: 0,
          stacking: null,
        },
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: CHARTS.exportEnabled,
        sourceWidth: 800,
        sourceHeight: 600, // Gets overridden by `updateExportSettings`
        filename: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
        chartOptions: {
          xAxis: {
            min: 0,
            max: 20, // Gets overridden by `updateExportSettings`
            scrollbar: {
              enabled: false,
            },
          },
          chart: {
            spacingTop: 50,
          },
        },
        buttons: {
          contextButton: {
            menuItems: CHARTS.exportButtons,
            x: scrollbarEnabled ? -25 : 0,
          },
        },
      },
      tooltip: {
        enabled: true,
        formatter() {
          if (tooltipFormatter) {
            return tooltipFormatter(this)
          }
          return `<b>${getOrdinalSuffix(this.x + 1)}</b><br/><span style="color:${
            this.point.color
          }">\u25CF</span>${this.point.name || this.point.key}: ${formatDecimalForDisplay(
            this.point.y,
            1
          )}%`
        },
      },
      series: seriesData,
      drilldown: {
        series: drilldownData,
        drillUpButton: {
          position: {
            x: -30,
            y: 0,
          },
        },
      },
    })

    if (seriesData && seriesData[0] && seriesData[0].data) {
      updateExportSettings(this.chart, seriesData[0].data.length)
    }
  }

  render() {
    return (
      <div
        className="chart-container col-md-12"
        ref={(c) => {
          this.chartContainerRef = c
        }}
      />
    )
  }
}

DrilldownBarChart.propTypes = {
  data: PropTypes.shape(DrilldownShape).isRequired,
  yAxisTitle: PropTypes.string.isRequired,
  xAxisTitle: PropTypes.string,
  scrollbarEnabled: PropTypes.bool,
  tooltipFormatter: PropTypes.func,
  t: PropTypes.func.isRequired,
  titleKey: PropTypes.string,
  subtitleKey: PropTypes.string
}

DrilldownBarChart.defaultProps = {
  xAxisTitle: null,
  scrollbarEnabled: false,
  tooltipFormatter: null,
  titleKey: "",
  subtitleKey: ""
}

export default translate()(DrilldownBarChart)



// WEBPACK FOOTER //
// ./src/components/charts/generic/DrilldownBarChart.jsx